import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { OrderStateEnums, OrderTypeEnums } from 'src/app/enum/order-enums';
import { CostCenterPriceModel } from 'src/app/models/archive/cost-center-price.model';
import { OrderDetailModel } from 'src/app/models/preliminaries/order-detail.model';
import { OrderModel } from 'src/app/models/preliminaries/order.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { CostCenterPriceService } from 'src/app/services/archive/cost-center-price.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-order-detail',
  templateUrl: './register-order-detail.component.html',
  styleUrls: ['./register-order-detail.component.scss'],
  standalone: false
})
export class RegisterOrderDetailComponent implements OnInit, OnChanges {

  @Input() costCenterPriceContract: CostCenterPriceModel = new CostCenterPriceModel();
  @Input() orderContract: OrderModel = new OrderModel();
  @Input() orderDetailContract: OrderDetailModel = new OrderDetailModel();
  @Input() orderDetails: OrderDetailModel[] = [];
  @Input() isCreatingOrderFromQuotation: boolean = false;
  @Input() swReservation: boolean = false;
  //Variables para el control del modal de Precios C.Costos:
  @Output() costCenterPriceRegister = new EventEmitter<CostCenterPriceModel>();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() openOrderPriceModal = new EventEmitter<number>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  codeNameArticle: string = '';
  rentalInvoicingConceptId: number = 0
  disableButtonSave: boolean = false;
  formOrderDetail: FormGroup = new FormGroup({});
  orderDetailId: number = 0;
  quantityFinal: number = 0;
  swGeneric: boolean = false;

  constructor(private articleService: ArticleService,
    private conceptInvoicingService: ConceptInvoicingService,
    private confirmDialogService: ConfirmDialogService,
    private costCenterPriceService: CostCenterPriceService,
    private dateConverterService: DateConverterService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private orderDetailService: OrderDetailService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.orderDetailContract);
  }

  async ngOnInit() {
    await this.getConceptInvoicingRental();
  }

  async ngOnChanges(changes: SimpleChanges) {
    const orderDetails = changes["orderDetails"];
    const orderDetailContract = changes['orderDetailContract'];
    const costCenterPriceContract = changes["costCenterPriceContract"];
    if (costCenterPriceContract && !costCenterPriceContract.firstChange) {
      await this.getCostCenterPriceRegister(costCenterPriceContract.currentValue);
      return;
    }
    if (orderDetails && !orderDetails.firstChange) {
      await this.createOrderDetails(this.orderDetails);
      return
    }
    if (orderDetailContract && !orderDetailContract.firstChange) {
      if (this.orderDetailContract.orderDetailId == 0) {
        this.createNewOrderDetail();
      } else {
        this.orderDetailId = this.orderDetailContract.orderDetailId;
        await this.validateForm(this.orderDetailContract);
      }
    }
  }

  applyControlState() {
    const isGeneric = this.swGeneric;
    const isOrderOpen = this.orderContract.orderStateId === OrderStateEnums.open;
    const isRental = this.orderContract.orderTypeId === OrderTypeEnums.rental;
    const shouldEnableReservation = isOrderOpen && isRental && !isGeneric;
    const notRental = this.orderContract.orderTypeId !== OrderTypeEnums.rental;
    const commonControls = ['workshopStock', 'shopStock', 'reservedStock', 'availableStock'];
    let controlsToDisable: string[] = [...commonControls];
    if (this.orderContract.orderStateId === OrderStateEnums.open) {
      controlsToDisable = [...controlsToDisable, 'quantityFinal'];
    } else if (this.orderContract.orderStateId === OrderStateEnums.initialClose) {
      controlsToDisable = [...controlsToDisable, 'compromiseDate', 'quantityInitial', 'quantityReserved'];
    }
    if (notRental || !this.swReservation || !shouldEnableReservation) {
      controlsToDisable = [...controlsToDisable, 'quantityReserved'];
    }
    const finalControls = [...new Set(controlsToDisable)];
    setTimeout(() => {
      this.formControlStateService.setControlState(this.formOrderDetail, finalControls, false);
    });
  }

  async changeQuantityInitial(quantityInitial: number) {
    const form = this.formOrderDetail.controls;
    const quantityRemission = this.orderDetailContract.quantityRemission ?? 0;
    const previousQuantityInitial = this.orderDetailContract.quantityInitial ?? 0;
    if (quantityInitial < quantityRemission) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad inicial no puede ser menor a la cantidad remisionada.'
      );
      form["quantityInitial"].patchValue(previousQuantityInitial);
      return;
    }
    form["quantityFinal"].patchValue(quantityInitial);
    this.orderDetailContract.quantityFinal = quantityInitial;
    const isRental = this.orderContract.orderTypeId === OrderTypeEnums.rental;
    if (isRental && this.swReservation && !this.swGeneric) {
      const currentAvailable = form["availableStock"].value ?? 0;
      const currentReservedStock = form["reservedStock"].value ?? 0;
      const previousReserved = this.orderDetailContract.quantityReserved ?? 0;
      const baseStock = currentAvailable + previousReserved;
      const newQuantityReserved = Math.min(baseStock, quantityInitial);
      form["quantityReserved"].patchValue(newQuantityReserved);
      const netReservedDifference = newQuantityReserved - previousReserved;
      form["availableStock"].patchValue(currentAvailable - netReservedDifference);
      form["reservedStock"].patchValue(currentReservedStock + netReservedDifference);
      this.orderDetailContract.quantityReserved = newQuantityReserved;
    }
    this.orderDetailContract.quantityInitial = quantityInitial;
  }

  changeQuantityFinal(quantityFinal: number) {
    const form = this.formOrderDetail.controls;
    const quantityInitial = form["quantityInitial"]?.value ?? 0;
    const previousQuantityFinal = this.orderDetailContract.quantityFinal ?? 0;
    const quantityRemission = this.orderDetailContract.quantityRemission ?? 0;
    const isRental = this.orderContract.orderTypeId === OrderTypeEnums.rental;
    if (quantityFinal > quantityInitial) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad final no puede ser mayor a la cantidad inicial.'
      );
      form["quantityFinal"].patchValue(previousQuantityFinal);
      return;
    }
    if (quantityFinal < quantityRemission) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad final no puede ser menor a la cantidad remisionada.'
      );
      form["quantityFinal"].patchValue(previousQuantityFinal);
      return;
    }
    this.orderDetailContract.quantityFinal = quantityFinal;
    if (isRental && this.swReservation && !this.swGeneric) {
      const currentAvailable = form["availableStock"].value ?? 0;
      const previousReserved = form["quantityReserved"]?.value ?? 0;
      const trueAvailability = currentAvailable + previousReserved;
      const newQuantityReserved = Math.min(quantityFinal, trueAvailability);
      const difference = previousReserved - newQuantityReserved;
      form["quantityReserved"].patchValue(newQuantityReserved);
      form["availableStock"].patchValue(currentAvailable + difference);
      const currentReservedStock = form["reservedStock"].value ?? 0;
      form["reservedStock"].patchValue(currentReservedStock - difference);
    }
  }

  async changeQuantityReserved(currentQuantityReserved: number) {
    const isRental = this.orderContract.orderTypeId === OrderTypeEnums.rental;
    if (isRental && this.swReservation && !this.swGeneric) {
      const form = this.formOrderDetail.controls;
      const quantityFinal = form["quantityFinal"].value ?? 0;
      const availableStock = form["availableStock"]?.value ?? 0;
      const previousReserved = this.orderDetailContract.quantityReserved ?? 0;
      const baseStock = availableStock + previousReserved;
      const maxLimit = Math.min(quantityFinal, baseStock);
      if (currentQuantityReserved === previousReserved) {
        return;
      }
      let errorMessage: string | null = null;
      if (currentQuantityReserved > quantityFinal) {
        errorMessage = `La reserva no puede superar la cantidad final (${quantityFinal}).`;
      } else if (currentQuantityReserved > baseStock) {
        errorMessage = `La cantidad a reservar excede el saldo disponible (${baseStock.toFixed(2)}).`;
      }
      if (errorMessage) {
        const accepted = await this.confirmDialogService.confirm({
          message: `${errorMessage} ¿Desea ajustar la reserva al máximo permitido (${maxLimit})?`,
          icon: 'pi pi-exclamation-triangle'
        });
        if (accepted) {
          currentQuantityReserved = maxLimit;
        } else {
          form["quantityReserved"].patchValue(previousReserved);
          return;
        }
      }
      const netReservedDifference = currentQuantityReserved - previousReserved;
      const newAvailable = availableStock - netReservedDifference;
      const currentReservedTotal = form["reservedStock"]?.value ?? 0;
      const newReservedTotal = currentReservedTotal + netReservedDifference;
      form["quantityReserved"].patchValue(currentQuantityReserved);
      form["availableStock"].patchValue(newAvailable);
      form["reservedStock"].patchValue(newReservedTotal);
      this.orderDetailContract.quantityReserved = currentQuantityReserved;
    }
  }

  createCostCenterPrice() {
    this.applyControlState();
    const costCenterPriceRegister = new CostCenterPriceModel();
    costCenterPriceRegister.costCenterId = this.orderContract.costCenterId;
    costCenterPriceRegister.articleId = this.orderDetailContract.articleId;
    this.costCenterPriceRegister.emit(costCenterPriceRegister);
  }

  async createOrderDetails(orderDetails: OrderDetailModel[]) {
    try {
      await firstValueFrom(this.orderDetailService.createOrderDetails(orderDetails, this.orderContract.orderId));
      this.toastHandlerService.showToast(
        'success',
        'Los detalles del pedido fueron creados exitosamente'
      );
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles del pedido.', error);
    }
  }

  async createOrderDetail(orderDetail: OrderDetailModel) {
    try {
      const response = await firstValueFrom(this.orderDetailService.createOrderDetail(orderDetail));
      this.toastHandlerService.showToast(
        'success',
        'El detalle del pedido fue creado exitosamente.'
      );
      const isNotRental = this.orderContract.orderTypeId !== OrderTypeEnums.rental;
      this.orderDetailId = response.orderDetailId;
      if (isNotRental || this.swGeneric) {
        this.openOrderPriceModal.emit(this.orderDetailId);
      }
    } catch (error) {
      console.error('Error al crear el detalle del pedido.', error);
    }
  }

  async createAndUpdateOrderDetail(orderDetail: OrderDetailModel) {
    if (this.orderDetailId === 0) {
      await this.createOrderDetail(orderDetail);
    } else {
      await this.updateOrderDetail(orderDetail);
    }
    this.clearForm();
    this.updateListDetail.emit(true);
  }

  async createNewOrderDetail() {
    this.orderDetailId = 0;
    this.costCenterPriceContract = new CostCenterPriceModel();
    await this.validateForm(this.orderDetailContract);
  }

  async clearForm() {
    this.codeNameArticle = '';
    this.isFormView.emit(false);
    this.validateForm(new OrderDetailModel())
  }

  async getArticle(articleId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.codeNameArticle = `${article.code} - ${article.name}`;
      this.swGeneric = article.swGeneric;
    } catch (error) {
      console.error('Error al obtener el artículo por Id.', error);
    }
  }

  async getCostCenterPriceByKeys(costCenterId: number, articleId: number) {
    try {
      if (!costCenterId || !articleId) {
        this.disableButtonSave = true;
        return;
      }
      const response = await firstValueFrom(this.costCenterPriceService.getCostCenterPriceByKeys(
        costCenterId, this.rentalInvoicingConceptId, articleId)
      );
      if (response.costCenterPriceId != 0 && response.baseUnitValue != -1) {
        this.disableButtonSave = false;
        this.costCenterPriceContract = response;
      } else if (response.costCenterPriceId == 0 && response.baseUnitValue != -1) {
        this.disableButtonSave = false;
        this.costCenterPriceContract = response;
      } else {
        this.disableButtonSave = true;
        this.toastHandlerService.showToast(
          'error',
          'El artículo NO se encuentra registrado en la lista de Precios.'
        );
      }
    } catch (error) {
      this.disableButtonSave = true;
      console.error('Error el precio por centro de costos.', error);
      throw error;
    }
  }

  async getCostCenterPriceRegister(costCenterPriceContract: CostCenterPriceModel) {
    if (costCenterPriceContract.costCenterPriceId) {
      this.orderDetailId = this.orderDetailContract.orderDetailId;
      this.orderDetailContract.costCenterPrice = costCenterPriceContract;
      await this.createAndUpdateOrderDetail(this.orderDetailContract);
    }
  }

  async getConceptInvoicingRental() {
    try {
      const response = await firstValueFrom(this.conceptInvoicingService.getConceptInvoicingAlquiler());
      this.rentalInvoicingConceptId = response.conceptInvoicingId;
    } catch (error) {
      console.error('Error al obtener el concepto de facturación para alquiler.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formOrderDetail.invalid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formOrderDetail.getRawValue() as OrderDetailModel;
    if (this.validateQuantities(model)) {
      this.orderDetailContract.orderDetailId = this.orderDetailId;
      this.orderDetailContract.articleId = model.articleId;
      this.orderDetailContract.quantityInitial = model.quantityInitial;
      this.orderDetailContract.quantityFinal = model.quantityFinal;
      this.orderDetailContract.compromiseDate = model.compromiseDate;
      if (this.swReservation) {
        this.orderDetailContract.quantityReserved = model.quantityReserved;
      }
      if (this.orderContract.orderStateId === OrderStateEnums.initialClose) {
        await this.createAndUpdateOrderDetail(this.orderDetailContract);
        return;
      }
      if (this.orderContract.orderStateId === OrderStateEnums.open) {
        if (this.swGeneric) {
          await this.createAndUpdateOrderDetail(this.orderDetailContract);
          return;
        }
        this.orderDetailContract.costCenterPrice = this.costCenterPriceContract;
        if (this.costCenterPriceContract.costCenterPriceId === 0 &&
          this.orderContract.orderTypeId === OrderTypeEnums.rental) {
          this.createCostCenterPrice();
          return;
        }
        await this.createAndUpdateOrderDetail(this.orderDetailContract);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      orderId: {
        required: 'El campo pedido es obligatorio.',
        min: 'Debes seleccionar un pedido válido.',
      },
      articleId: {
        required: 'El campo artículo es obligatorio.',
        min: 'Debes seleccionar un artículo válido.',
      },
      quantityInitial: {
        required: 'El campo Cant.Ini. es obligatorio.',
        min: 'La Cant.Ini. mínima permitida es 1.',
        max: 'La Cant.Ini. no puede superar 9.999.999,99.',
      },
      quantityReserved: {
        required: 'El campo Cant.Reserva es obligatorio.',
        min: 'La Cant.Reserva mínima permitida es 0.',
        max: 'La Cant.Reserva no puede superar 9.999.999,99.',
      },
      quantityFinal: {
        min: 'La Cant.Fin. mínima permitida es 1.',
        max: 'La Cant.Fin. no puede superar 9.999.999,99.',
      },
    };
    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formOrderDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateOrderDetail(orderDetail: OrderDetailModel) {
    try {
      await firstValueFrom(this.orderDetailService.updateOrderDetail(orderDetail));
      this.toastHandlerService.showToast(
        'success',
        'El detalle del pedido fue modificado exitosamente.'
      );
    } catch (error) {
      console.error('Error al modificar el detalle del pedido.', error);
    }
  }

  updateReservationControlState(isNotGeneric: boolean) {
    const isOrderOpen = this.orderContract.orderStateId === OrderStateEnums.open;
    const isRental = this.orderContract.orderTypeId === OrderTypeEnums.rental;
    const shouldEnableReservation = isOrderOpen && isRental && isNotGeneric;
    this.formControlStateService.setControlState(this.formOrderDetail, ['quantityReserved'], shouldEnableReservation);
  }

  async validateForm(orderDetail: OrderDetailModel) {
    this.formOrderDetail = this.formBuilder.group({
      shopStock: [orderDetail.shopStock],
      workshopStock: [orderDetail.workshopStock],
      reservedStock: [orderDetail.reservedStock],
      availableStock: [orderDetail.availableStock],
      areaM2: [orderDetail.areaM2, [Validators.required]],
      orderId: [orderDetail.orderId, [Validators.required, Validators.min(1)]],
      articleId: [orderDetail.articleId, [Validators.required, Validators.min(1)]],
      rentalUnitId: [orderDetail.rentalUnitId, [Validators.required, Validators.min(1)]],
      quantityFinal: [orderDetail.quantityFinal, [Validators.min(1), Validators.max(9999999.99)]],
      quantityReserved: [orderDetail.quantityReserved, [Validators.min(0), Validators.max(9999999.99)]],
      quantityInitial: [orderDetail.quantityInitial, [Validators.required, Validators.min(1), Validators.max(9999999.99)]],
      compromiseDate: [orderDetail.compromiseDate ? this.dateConverterService.convertUtcToLocalDate(orderDetail.compromiseDate)
        : null, [Validators.required]],
    });
    const articleId = orderDetail.articleId;
    const costCenterId = this.orderContract.costCenterId;
    if (articleId > 0) {
      this.isFormView.emit(true);
      await Promise.all([
        this.getArticle(articleId),
        this.getCostCenterPriceByKeys(costCenterId, articleId)
      ]);
    }
    this.applyControlState();
  }

  validateFormControls() {
    this.formOrderDetail.controls["quantityFinal"].enable();
    Object.keys(this.formOrderDetail.controls).forEach((key) => {
      const control = this.formOrderDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

  validateQuantities(model: OrderDetailModel): boolean {
    const form = this.formOrderDetail.controls;
    const availableStock = form["availableStock"].value ?? 0;
    if (model.quantityInitial < this.orderDetailContract.quantityRemission) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad inicial no puede ser menor a la cantidad remisionada.'
      );
      return false;
    }
    if (model.quantityFinal > model.quantityInitial) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad final no puede ser mayor a la cantidad inicial.'
      );
      return false;
    }
    if (model.quantityFinal < this.orderDetailContract.quantityRemission) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad final no puede ser menor a la cantidad remisionada.'
      );
      return false;
    }
    if (this.swReservation && !this.swGeneric) {
      const previousReserved = this.orderDetailContract.quantityReserved ?? 0;
      const baseStock = availableStock + previousReserved;
      if (model.quantityReserved > model.quantityFinal) {
        this.toastHandlerService.showToast(
          'error',
          `La reserva no puede superar la cantidad final (${model.quantityFinal}).`
        );
        return false;
      }
      if (model.quantityReserved > baseStock) {
        this.toastHandlerService.showToast(
          'error',
          `La cantidad a reservar excede el saldo disponible (${baseStock.toFixed(2)}).`
        );
        return false;
      }
    }
    return true;
  }
}